import { useCallback, useEffect, useRef, useState } from "react";
import { X, Search, Loader2 } from "lucide-react";
import { fetchTrendingGifs, searchGifs, registerGifShare } from "../../lib/gifs";
import { logSwallow } from "../../lib/log";

/**
 * GifPicker — reusable Klipy GIF picker bottom-sheet.
 *
 * Used identically by the Live Room composer, the Vibe comment composer, and
 * the private Chat composer. Props:
 *   open      — boolean, controls visibility
 *   onClose   — () => void
 *   onSelect  — (gif) => void  where gif = {gif_url, gif_preview_url, gif_id,
 *               gif_slug, gif_width, gif_height, gif_source}
 *
 * Attribution: the search input placeholder reads "Search KLIPY" as required.
 * Moderation is enforced entirely server-side (content_filter=high) — this
 * component never sends a rating.
 */
export default function GifPicker({ open, onClose, onSelect }) {
  const [gifs, setGifs] = useState([]);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const activeQueryRef = useRef("");

  const load = useCallback(async (query, pg, append) => {
    setLoading(true);
    setError("");
    activeQueryRef.current = query;
    try {
      const data = query
        ? await searchGifs(query, pg, 24)
        : await fetchTrendingGifs(pg, 24);
      // Guard against out-of-order responses when the user types fast.
      if (activeQueryRef.current !== query) return;
      setGifs((prev) => (append ? [...prev, ...(data.gifs || [])] : data.gifs || []));
      setHasNext(!!data.has_next);
      setPage(data.page || pg);
    } catch (e) {
      logSwallow("gifpicker.load", e);
      setError("Couldn't load GIFs. Try again.");
      if (!append) setGifs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load trending each time the sheet opens; reset on close.
  useEffect(() => {
    if (open) {
      setQ("");
      setGifs([]);
      setPage(1);
      load("", 1, false);
    }
  }, [open, load]);

  // Debounced search as the user types.
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => {
      load(q.trim(), 1, false);
    }, 350);
    return () => clearTimeout(t);
  }, [q, open, load]);

  const pick = (g) => {
    registerGifShare(g);
    onSelect?.(g);
    onClose?.();
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center bg-black/70 backdrop-blur-sm"
      data-testid="gif-picker-overlay"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-[#0A0A0A] border-t border-[#E60000]/40 rounded-t-3xl shadow-[0_-8px_60px_rgba(230,0,0,0.25)] flex flex-col"
        style={{ height: "70vh" }}
        onClick={(e) => e.stopPropagation()}
        data-testid="gif-picker"
      >
        <div className="flex items-center gap-2 p-3 border-b border-white/10">
          <div className="flex-1 flex items-center gap-2 bg-black/60 border border-white/10 rounded-full px-4 py-2 focus-within:border-[#E60000]">
            <Search size={16} className="text-zinc-500" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search KLIPY"
              className="flex-1 bg-transparent outline-none text-white text-sm"
              style={{ fontSize: "16px" }}
              data-testid="gif-search-input"
            />
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-zinc-300"
            data-testid="gif-picker-close"
            aria-label="Close GIF picker"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-3" data-testid="gif-grid">
          {error && (
            <div className="text-center text-zinc-400 text-sm py-8" data-testid="gif-error">{error}</div>
          )}
          {!error && gifs.length === 0 && !loading && (
            <div className="text-center text-zinc-500 text-sm py-8" data-testid="gif-empty">No GIFs found.</div>
          )}
          <div className="columns-2 gap-2 [column-fill:_balance]">
            {gifs.map((g, i) => (
              <button
                key={`${g.gif_id || g.gif_slug || "gif"}-${i}`}
                onClick={() => pick(g)}
                className="mb-2 w-full block overflow-hidden rounded-xl border border-white/5 hover:border-[#E60000]/60 transition group"
                data-testid={`gif-item-${i}`}
              >
                <img
                  src={g.gif_preview_url || g.gif_url}
                  alt={g.title || "GIF"}
                  loading="lazy"
                  className="w-full h-auto block group-hover:opacity-90"
                />
              </button>
            ))}
          </div>
          {loading && (
            <div className="flex justify-center py-4" data-testid="gif-loading">
              <Loader2 size={22} className="animate-spin text-[#E60000]" />
            </div>
          )}
          {!loading && hasNext && gifs.length > 0 && (
            <button
              onClick={() => load(activeQueryRef.current, page + 1, true)}
              className="w-full mt-2 py-2.5 rounded-full border border-white/10 text-zinc-300 text-xs font-mono-accent hover:border-[#E60000]/40 hover:text-white transition"
              data-testid="gif-load-more"
            >
              LOAD MORE
            </button>
          )}
        </div>

        <div className="px-4 py-2 border-t border-white/5 text-center">
          <span className="text-[10px] font-mono-accent text-zinc-600 tracking-wider">POWERED BY KLIPY</span>
        </div>
      </div>
    </div>
  );
}
